import { Organization, Role, User } from '@/models/user';
import { API, hasBackend } from './api';
import { mockUser } from './mock-data';
import { getOrganization } from '@/data/organizations';
import {
  CreateSSOOrganizationRequest,
  CreateLegacyOrganizationRequest,
  SeatInfoResponse,
  UserListResponse,
  UserActionResponse,
  AvailableRolesResponse,
  UpdateSsoRolesResponse,
} from '@/models/sso';

export const AdminService = {
  getOrganizations: async (): Promise<Array<Organization>> => {
    try {
      const response = (await API.get('/organizations')) as {
        data: {
          status: string;
          organization_count: number;
          organizations: Array<{
            organization_id: string;
            name: string;
            created_at: string;
            updated_at: string;
            id: string;
            seat_limit?: number;
            seats_used?: number;
            auth_method?: 'password' | 'sso';
            zitadel_org_id?: string;
            subdomain?: string;
          }>;
        };
      };

      if (response.data.status === 'success') {
        return response.data.organizations.map((org) => ({
          ...getOrganization(org.id),
          id: org.organization_id,
          name: org.name,
          created_at: org.created_at,
          seat_limit: org.seat_limit,
          seats_used: org.seats_used,
          auth_method: org.auth_method,
          zitadel_org_id: org.zitadel_org_id,
          subdomain: org.subdomain,
        }));
      } else {
        throw new Error('Failed to fetch organizations');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      throw new Error('An error occurred while fetching users');
    }
  },
  addOrganization: async (
    data: CreateSSOOrganizationRequest | CreateLegacyOrganizationRequest
  ): Promise<boolean> => {
    if (!hasBackend) {
      throw new Error('Unable to add new organizations in mock mode.');
    }

    try {
      const response = (await API.post('/organizations', data)) as {
        data: {
          status: string;
          message: string;
          organization: {
            organization_id: string;
            name: string;
            created_at: string;
            updated_at: string;
            id: string;
          };
        };
      };

      if (response.data.status === 'success') {
        return true;
      } else {
        throw new Error('Failed to create organization');
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      const err = error as { response?: { data?: { detail?: string } } };
      if (err.response?.data?.detail) {
        throw new Error(err.response.data.detail);
      }
      throw new Error('An error occurred while creating the organization');
    }
  },
  updateOrganization: async (
    orgID: string,
    updates: { name?: string; seat_limit?: number }
  ): Promise<boolean> => {
    if (!hasBackend) {
      throw new Error('Unable to update organizations in mock mode.');
    }

    try {
      const response = (await API.put(
        `/organizations/id/${orgID}`,
        updates
      )) as {
        data: {
          status: string;
          message: string;
        };
      };

      if (response.data.status === 'success') {
        return true;
      } else {
        throw new Error('Failed to update organization');
      }
    } catch (error) {
      console.error('Error updating organization:', error);
      throw new Error('An error occurred while updating the organization');
    }
  },
  getUsers: async (orgID?: string): Promise<User[]> => {
    if (!hasBackend) {
      return [
        mockUser,
        {
          id: 'ae8a45c6-8f89-46b7-a051-c727d3efdb02',
          email: 'f.doe@ipcinfo.org',
          name: 'Jane Doe',
          roles: [Role.USER],
          organizations: [
            getOrganization('ae8a45c6-8f89-46b7-a051-c727d3efdb02'),
          ],
        },
      ];
    }

    try {
      const users: User[] = [];

      const orgUsersResponse = (await API.get('/users/organization', {
        params: {
          organization_id: orgID,
        },
      })) as {
        data: {
          status: string;
          user_count: number;
          users: Array<{
            user_id: string;
            name: string;
            email: string;
            organization_id: string;
            roles: Array<Role>;
            created_at: string;
            updated_at: string;
            id: string;
          }>;
        };
      };

      if (orgUsersResponse.data.status === 'success') {
        users.push(
          ...orgUsersResponse.data.users.map((user) => ({
            id: user.user_id,
            email: user.email,
            name: user.name,
            roles: user.roles,
            organizations: [getOrganization(user.organization_id)],
            created_at: user.created_at,
          }))
        );
      } else {
        throw new Error('Failed to fetch users');
      }

      return users;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw new Error('An error occurred while fetching users');
    }
  },
  addUser: async (
    email: string,
    password: string,
    name: string,
    orgID?: string
  ): Promise<boolean> => {
    if (!hasBackend) {
      throw new Error('Unable to add new users in mock mode.');
    }

    try {
      const response = (await API.post('/users', {
        email,
        password,
        name,
        organization_id: orgID,
      })) as {
        data: {
          status: string;
          message: string;
          user: {
            user_id: string;
            name: string;
            email: string;
            organization_id: string;
            roles: Array<Role>;
            created_at: string;
            updated_at: string;
            id: string;
          };
        };
      };

      if (response.data.status === 'success') {
        return true;
      } else {
        throw new Error('Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      throw new Error('An error occurred while creating the user');
    }
  },
  getAvailableRoles: async (userID: string): Promise<Role[]> => {
    const response = (await API.get(`/users/${userID}/roles`)) as {
      data: {
        status: string;
        user_id: string;
        roles: Role[];
      };
    };

    return response.data.roles;
  },
  updateRoles: async (userID: string, newRoles: Role[]): Promise<boolean> => {
    const response = (await API.put(`/users/${userID}/roles`, {
      roles: newRoles,
    })) as {
      data: {
        status: string;
      };
    };

    return response.data.status === 'success';
  },

  /**
   * Get seat limit information for an organization.
   * Requires org-admin role.
   */
  getSeatInfo: async (orgID: string): Promise<SeatInfoResponse> => {
    if (!hasBackend) {
      // Mock response for development
      return {
        seat_limit: 20,
        seats_used: 5,
        seats_available: 15,
      };
    }

    try {
      const response = await API.get<SeatInfoResponse>(
        `/organizations/${orgID}/seats`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching seat info:', error);
      throw new Error('An error occurred while fetching seat information');
    }
  },

  /**
   * Get all users in an SSO organization with status.
   * Fetches from Zitadel via backend, merged with MongoDB members.
   * Requires org-admin role.
   */
  getOrgUsers: async (orgID: string): Promise<UserListResponse> => {
    if (!hasBackend) {
      return {
        users: [],
        total: 0,
        active_count: 0,
        inactive_count: 0,
        seat_limit: 20,
        seats_available: 20,
      };
    }

    try {
      const response = await API.get<UserListResponse>(
        `/organizations/${orgID}/users`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw new Error('An error occurred while fetching users');
    }
  },

  /**
   * Activate a user (create member, reserve seat).
   * Requires org-admin role.
   */
  activateUser: async (
    orgID: string,
    zitadelUserId: string
  ): Promise<UserActionResponse> => {
    if (!hasBackend) {
      throw new Error('Unable to activate users in mock mode.');
    }

    try {
      const response = await API.post<UserActionResponse>(
        `/organizations/${orgID}/users/${zitadelUserId}/activate`
      );
      return response.data;
    } catch (error) {
      console.error('Error activating user:', error);
      const err = error as {
        response?: { status: number; data?: { detail?: string } };
      };
      if (err.response?.status === 403) {
        throw new Error(err.response.data?.detail || 'Seat limit reached');
      }
      throw new Error('An error occurred while activating the user');
    }
  },

  /**
   * Deactivate a user (delete member, free seat).
   * Requires org-admin role.
   */
  deactivateUser: async (
    orgID: string,
    zitadelUserId: string
  ): Promise<UserActionResponse> => {
    if (!hasBackend) {
      throw new Error('Unable to deactivate users in mock mode.');
    }

    try {
      const response = await API.post<UserActionResponse>(
        `/organizations/${orgID}/users/${zitadelUserId}/deactivate`
      );
      return response.data;
    } catch (error) {
      console.error('Error deactivating user:', error);
      throw new Error('An error occurred while deactivating the user');
    }
  },

  /**
   * Get available roles for an SSO organization.
   * Fetches roles dynamically from Zitadel project.
   * This is different from getAvailableRoles which fetches a user's current roles.
   * Requires org-admin role.
   */
  getOrgAvailableRoles: async (orgID: string): Promise<Role[]> => {
    if (!hasBackend) {
      // Return default roles for mock mode
      return [Role.USER, Role.ORG_DATA_ADMIN, Role.ORG_USER_ADMIN];
    }

    try {
      const response = await API.get<AvailableRolesResponse>(
        `/organizations/${orgID}/available-roles`
      );
      if (response.data.status !== 'success') {
        throw new Error('Failed to fetch available roles');
      }
      return response.data.roles;
    } catch (error) {
      console.error('Error fetching available roles:', error);
      throw new Error('An error occurred while fetching available roles');
    }
  },

  /**
   * Update roles for an SSO user.
   * Note: This replaces ALL roles - any roles not included will be revoked.
   * Requires org-admin role.
   */
  updateSsoUserRoles: async (
    orgID: string,
    zitadelUserId: string,
    roles: string[]
  ): Promise<UpdateSsoRolesResponse> => {
    if (!hasBackend) {
      throw new Error('Unable to update roles in mock mode.');
    }

    try {
      const response = await API.put<UpdateSsoRolesResponse>(
        `/organizations/${orgID}/users/${zitadelUserId}/roles`,
        { roles }
      );
      if (response.data.status !== 'success') {
        throw new Error(response.data.message || 'Failed to update user roles');
      }
      return response.data;
    } catch (error) {
      console.error('Error updating user roles:', error);
      const err = error as {
        response?: { status: number; data?: { detail?: string } };
      };
      if (err.response?.data?.detail) {
        throw new Error(err.response.data.detail);
      }
      throw new Error('An error occurred while updating user roles');
    }
  },
};
